import { useCallback, useEffect } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type { CrmMessageBatchHistoryItem, CrmMessageUsageStats } from "../types";
import {
  CRM_MESSAGE_FRANCHISE_EMPTY_STATS,
  type CrmMessageFranchiseUsageData,
  crmMessageFranchiseStatsService,
} from "../services/crmMessageFranchiseStatsService";

const INITIAL_FRANCHISE_USAGE_DATA: CrmMessageFranchiseUsageData = {
  stats: CRM_MESSAGE_FRANCHISE_EMPTY_STATS,
  batchHistory: [],
};

export interface UseCrmMessageFranchiseStatsResult {
  stats: CrmMessageUsageStats;
  batchHistory: CrmMessageBatchHistoryItem[];
  isLoading: boolean;
  loadError: unknown;
  refetch: () => void;
}

/**
 * Carrega métricas de franquia (disponíveis / enviadas / contratadas) via FHIR,
 * com a mesma regra do legado `CRMQuickStats` + `CRMContractContext`.
 *
 * @param refreshSignal — incremente após concluir compra/pagamento para forçar nova leitura FHIR.
 */
export function useCrmMessageFranchiseStats(
  organizationId: string | undefined,
  refreshSignal: number = 0,
): UseCrmMessageFranchiseStatsResult {
  const fetchStats = useCallback((orgId: string) => {
    return crmMessageFranchiseStatsService.fetchCrmMessageFranchiseUsageData(orgId);
  }, []);

  const { run, loading, data, error, reset } = useAsyncTask<[string], CrmMessageFranchiseUsageData>({
    task: fetchStats,
    initialData: INITIAL_FRANCHISE_USAGE_DATA,
  });

  useEffect(() => {
    const trimmed = organizationId?.trim();
    if (!trimmed) {
      reset();
      return;
    }
    void run(trimmed);
  }, [organizationId, refreshSignal, run, reset]);

  const refetch = useCallback(() => {
    const trimmed = organizationId?.trim();
    if (trimmed) {
      void run(trimmed);
    }
  }, [organizationId, run]);

  return {
    stats: data?.stats ?? CRM_MESSAGE_FRANCHISE_EMPTY_STATS,
    batchHistory: data?.batchHistory ?? [],
    isLoading: loading,
    loadError: error,
    refetch,
  };
}
